import React, { useState } from 'react';
import type { GameMode } from '../game/types';

interface HelpModalProps {
  onClose: () => void;
  contextMode?: GameMode | null;
}

const MODES: { id: GameMode; label: string }[] = [
  { id: 'classic', label: 'Classic Mode' },
  { id: 'blitz', label: 'Blitz Mode' },
  { id: 'versus', label: 'Versus Mode' },
];

export const HelpModal: React.FC<HelpModalProps> = ({ onClose, contextMode = null }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const activeMode: GameMode = contextMode ?? MODES[currentIndex].id;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : MODES.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < MODES.length - 1 ? prev + 1 : 0));
  };

  const getHeaderTitle = () => {
    if (!contextMode) return 'How to Play';
    if (contextMode === 'blitz') return 'Blitz Mode';
    if (contextMode === 'versus') return 'Versus Mode';
    return 'Classic Mode';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content help-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="help-header">
          <h2>{getHeaderTitle()}</h2>
          {!contextMode && (
            <div className="help-nav-bar">
              <button
                className="help-nav-arrow"
                id="btn-help-prev"
                onClick={handlePrev}
                aria-label="Previous mode"
              >
                ‹
              </button>
              <div className="help-nav-title-group">
                <span className="help-nav-title">{MODES[currentIndex].label}</span>
                <div className="help-dots">
                  {MODES.map((m, idx) => (
                    <span
                      key={m.id}
                      className={`help-dot ${idx === currentIndex ? 'active' : ''}`}
                      onClick={() => setCurrentIndex(idx)}
                      role="button"
                      aria-label={`Go to ${m.label}`}
                    />
                  ))}
                </div>
              </div>
              <button
                className="help-nav-arrow"
                id="btn-help-next"
                onClick={handleNext}
                aria-label="Next mode"
              >
                ›
              </button>
            </div>
          )}
        </div>

        <div className="help-scroll-area">
          {activeMode === 'classic' ? (
            <>
              <section className="help-section">
                <h3 className="help-heading">What is a SET?</h3>
                <p className="help-text">
                  Select <strong>3 cards</strong> where each feature is <strong>all the same</strong> or <strong>all different</strong>:
                </p>
                <div className="help-feature-grid">
                  <div className="feature-item">
                    <span className="feature-label">Shape</span>
                    <span className="feature-desc">Bolt • Eye • Kite</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-label">Color</span>
                    <span className="feature-desc">Red • Green • Purple</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-label">Count</span>
                    <span className="feature-desc">1 • 2 • 3</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-label">Shading</span>
                    <span className="feature-desc">Solid • Striped • Open</span>
                  </div>
                </div>
                <p className="help-hint-rule">
                  <strong>Rule:</strong> If 2 cards match and 1 doesn't for any feature, it's <strong>not a set</strong>.
                </p>
              </section>

              <section className="help-section">
                <h3 className="help-heading">Game Rules</h3>
                <ul className="help-list">
                  <li>
                    <strong>Guaranteed Sets:</strong> The board always has at least one valid set. If none exist, it automatically reshuffles.
                  </li>
                  <li>
                    <strong>Game Over:</strong> Find sets until all 81 cards are cleared. Fastest times are saved to the Leaderboard.
                  </li>
                </ul>
              </section>
            </>
          ) : activeMode === 'blitz' ? (
            <section className="help-section">
              <h3 className="help-heading">Blitz Rules</h3>
              <ul className="help-list">
                <li>
                  <strong>30-Second Start:</strong> You start with 30 seconds on the clock.
                </li>
                <li>
                  <strong>+10s Bonus:</strong> Every valid SET you find adds <strong>+10 seconds</strong> to your timer.
                </li>
                <li>
                  <strong>Sudden Death:</strong> Selecting 3 cards that do not form a set is an <strong>instant game over</strong>!
                </li>
                <li>
                  <strong>No Hints:</strong> No hints are available—speed and accuracy are everything.
                </li>
                <li>
                  <strong>Goal:</strong> Find as many sets as possible before time expires, or clear the entire deck!
                </li>
              </ul>
            </section>
          ) : (
            <section className="help-section">
              <h3 className="help-heading">Versus Rules</h3>
              <ul className="help-list">
                <li>
                  <strong>How to Buzz:</strong> Tap your team's colored buzzer (Blue on left, Red on right in landscape; left half vs right half in portrait) to lock out your opponent and activate the board.
                </li>
                <li>
                  <strong>5-Second Timeout:</strong> Once buzzed in, you have <strong>5 seconds</strong> to select 3 cards. If the timer runs out before selecting 3 cards, your turn is forfeited with a <strong>-1 point penalty</strong> (minimum 0).
                </li>
                <li>
                  <strong>Scoring:</strong>
                  <ul style={{ paddingLeft: '14px', marginTop: '4px' }}>
                    <li><strong>Valid SET:</strong> <strong>+1 point</strong>, cards are replenished, buzzers unlock for both teams.</li>
                    <li><strong>Invalid SET:</strong> <strong>-1 point penalty</strong> (minimum 0), buzzers unlock for both teams.</li>
                  </ul>
                </li>
                <li>
                  <strong>Win Condition:</strong> First team to reach <strong>14 sets</strong> wins immediately, or the team with the most sets when the deck runs out.
                </li>
              </ul>
            </section>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-primary" id="btn-close-help" onClick={onClose}>
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};

